import { getConfiguration } from "./bagConfiguration";
import clientConfig from "../../template/clientConfig";
import { eventBus } from "../../helpers/eventing/events";
import log from "../../helpers/logger";
import promiseWithTimeout from "../../helpers/promiseWithTimeout";
import {
  asosGlobalNamespace,
  sdkGlobalNamespace
} from "../../template/constants";
import { noticeError } from "@src/helpers/monitoring";

const MINIBAG_BAG_EXPANSIONS = ["getdeliveryoptions"];

const withMinibagExpansions = (additionalExpansions, shouldExpand) =>
  shouldExpand()
    ? [...(additionalExpansions || []), ...MINIBAG_BAG_EXPANSIONS]
    : additionalExpansions;

const wrapWithMinibagExpansions = (bagSdk, shouldExpand) => {
  const originalAddVariant = bagSdk.addVariant.bind(bagSdk);
  const originalMoveToBag = bagSdk.moveToBag.bind(bagSdk);
  const originalSaveForLater = bagSdk.saveForLater.bind(bagSdk);

  bagSdk.addVariant = (item, additionalExpansions, context) =>
    originalAddVariant(
      item,
      withMinibagExpansions(additionalExpansions, shouldExpand),
      context
    );

  bagSdk.moveToBag = (...args) => {
    const expansionsIndex = typeof args[0] === "number" ? 2 : 1;
    const wrappedArgs = [...args];
    wrappedArgs[expansionsIndex] = withMinibagExpansions(
      args[expansionsIndex],
      shouldExpand
    );

    return originalMoveToBag(...wrappedArgs);
  };

  bagSdk.saveForLater = (itemId, additionalExpansions) =>
    originalSaveForLater(
      itemId,
      withMinibagExpansions(additionalExpansions, shouldExpand)
    );

  return bagSdk;
};

export const initialise = ({ context, authClient, shouldExpand }) =>
  new Promise(resolve =>
    requirejs(["bagSdk/sdk"], sdk =>
      resolve(
        wrapWithMinibagExpansions(
          sdk.getInstance(context, getConfiguration({ authClient })),
          shouldExpand
        )
      )
    )
  );

export const waitForBagSDK = () =>
  new Promise((resolve, reject) => {
    if (clientConfig.isServer()) {
      return reject(new Error("Bag SDK not available on server"));
    }

    const {
      /* istanbul ignore next */ [asosGlobalNamespace]:
        /* istanbul ignore next */ {
          [sdkGlobalNamespace]: /* istanbul ignore next */ {
            bag
          } = /* istanbul ignore next */ {}
        } = /* istanbul ignore next */ {}
    } = window;
    if (bag) {
      resolve(bag);
    } else {
      eventBus.addListener("bag-sdk-ready", resolve);
    }
  });

export const getBagSummary = () =>
  waitForBagSDK()
    .then(bag => bag.getBagSummary())
    .catch(error => {
      if (error) {
        noticeError(error);
      }
      throw error;
    });

export const removeItem = (itemId, itemType) =>
  waitForBagSDK()
    .then(bag =>
      promiseWithTimeout(
        bag.removeItem(itemId, itemType, MINIBAG_BAG_EXPANSIONS)
      )
    )
    .catch(error => noticeError(error));

export const getBag = () =>
  waitForBagSDK()
    .then(bag => bag.getBag(MINIBAG_BAG_EXPANSIONS))
    .catch(error => noticeError(error));

export const getBagById = bagId =>
  waitForBagSDK()
    .then(bag => bag.getBagById(bagId, MINIBAG_BAG_EXPANSIONS))
    .catch(error => {
      noticeError(error);
      throw error;
    });

export const subscribeToUpdates = callback =>
  waitForBagSDK()
    .then(bag => {
      $(bag).on(
        "bagUpdated:completed",
        (
          _event,
          _eventType,
          { bag, messages },
          { sellerChanged, containsHotProduct } = {}
        ) => {
          callback({ bag, messages, sellerChanged, containsHotProduct });
        }
      );
    })
    .catch(/* istanbul ignore next */ log.error);

export const subscribeToError = callback =>
  waitForBagSDK().then(bag => {
    $(bag).on(
      "addToBag:error",
      (_event, _error, { containsHotProduct, initiator } = {}) => {
        callback({
          containsHotProduct,
          initiator
        });
      }
    );
  });

export const startCheckout = () =>
  waitForBagSDK()
    .then(bag => bag.startCheckout())
    .catch(error => noticeError(error));
